import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger.js";
import lottie from "lottie-web";

gsap.registerPlugin(ScrollTrigger);

window.gsap = gsap;

// == hero logo-reveal animation: plays once over the static hero image.
// Uses inline animationData (via window.heroLogoRevealData, set by a
// classic <script> tag in the template) rather than lottie's `path` option --
// the static asset host is cross-origin from the page and doesn't send CORS
// headers, so the XHR fetch behind `path` gets blocked; a <script> load isn't
// subject to CORS.
if (window.heroLogoRevealData) {
    document.querySelectorAll(".parade-2026-hero-lottie").forEach((container) => {
        lottie.loadAnimation({
            container,
            renderer: "svg",
            loop: false,
            autoplay: true,
            animationData: window.heroLogoRevealData,
        });
    });
}

// == hover-to-pause overlay on parade 2026 videos
document.querySelectorAll(".parade-2026-video-box").forEach((videoBox) => {
    const cover = videoBox.querySelector(".video-cover");
    const video = videoBox.querySelector("video") || videoBox.querySelector("iframe");
    if (!cover || !video) {
        return;
    }
    const playIcon = cover.querySelector(".video-icon-play");
    const pauseIcon = cover.querySelector(".video-icon-pause");
    const videoId = video.id;

    playIcon?.addEventListener("click", () => {
        videoBox.classList.add("is-playing");
        oi.video.play(videoId);
    });

    pauseIcon?.addEventListener("click", () => {
        videoBox.classList.remove("is-playing");
        oi.video.pause(videoId);
    });

    if (video.tagName === "VIDEO") {
        video.addEventListener("play", () => videoBox.classList.add("is-playing"));
        video.addEventListener("pause", () => videoBox.classList.remove("is-playing"));
    }
});

// == hero header: the logo column (.header-grid-box) needs the same blurred backdrop
// as the homes offcanvas while it's open, but it's a sibling of the toggle
// button rather than an ancestor/descendant, so CSS attribute selectors can't
// reach it -- mirror the offcanvas's open state onto it directly. The trigger
// button also needs its aria-expanded (and burger->X animation) kept in sync
// manually, since Bootstrap only manages that attribute for collapse triggers.
const paradeHomesOffcanvasEl = document.getElementById("paradeHomesOffcanvas");
const headerColLeft = document.querySelector(".header-grid-box");
const paradeMenuTrigger = document.querySelector(".custom-menu-trigger");
if (paradeHomesOffcanvasEl) {
    paradeHomesOffcanvasEl.addEventListener("show.bs.offcanvas", () => {
        headerColLeft?.classList.add("is-menu-open");
        paradeMenuTrigger?.setAttribute("aria-expanded", "true");
    });
    paradeHomesOffcanvasEl.addEventListener("hide.bs.offcanvas", () => {
        headerColLeft?.classList.remove("is-menu-open");
        paradeMenuTrigger?.setAttribute("aria-expanded", "false");
    });
}

// == mobile "the homes" sub-nav dropdown: reflect the clicked home in the
// collapsed toggle label and close the menu back up
const subNavCollapseEl = document.getElementById("subNavCollapse");
const subNavActiveLabel = document.getElementById("subNavActiveLabel");
if (subNavCollapseEl && subNavActiveLabel) {
    subNavCollapseEl.querySelectorAll(".sub-nav-dropdown-link").forEach((link) => {
        link.addEventListener("click", (event) => {
            // The collapse sits above the target sections, so closing it after
            // a native anchor jump shifts the whole page up and scrolls past the
            // target. Scroll manually, only once the collapse has fully closed.
            event.preventDefault();
            const targetEl = document.getElementById(link.getAttribute("href").slice(1));
            subNavActiveLabel.textContent = link.textContent;
            subNavCollapseEl.addEventListener("hidden.bs.collapse", () => {
                targetEl?.scrollIntoView({ behavior: "smooth" });
            }, { once: true });
            bootstrap.Collapse.getOrCreateInstance(subNavCollapseEl).hide();
        });
    });
}

// == desktop "the homes" sub-nav: a draggable/swipeable horizontal strip
// instead of a wrapping flex row, since 8 home names can be wider than the
// container at the low end of xl+ viewports.
const subNavSwiperEl = document.querySelector(".subNavSwiper");
if (subNavSwiperEl) {
    new Swiper(subNavSwiperEl, {
        slidesPerView: "auto",
        spaceBetween: 0,
        freeMode: true,
        grabCursor: true,
    });
}

// == "50-50 variant" home showcase: clicking a thumbnail swaps the main image
// and updates the "N / total" counter. Scoped per .showcase-media so each
// parade home's showcase (there can be several on the page) works independently.
document.querySelectorAll(".parade-2026-50-50-varient-1 .showcase-media").forEach((media) => {
    const mainImg = media.querySelector(".main-img-wrap .showcase-img-main");
    const counter = media.querySelector(".showcase-thumb-counter");
    const thumbs = Array.from(media.querySelectorAll(".showcase-thumb"));
    if (!mainImg || !thumbs.length) {
        return;
    }

    function setActive(index) {
        thumbs.forEach((thumb, i) => thumb.classList.toggle("is-active", i === index));
        if (counter) {
            counter.textContent = `${index + 1} / ${thumbs.length}`;
        }
    }

    thumbs.forEach((thumb, index) => {
        thumb.addEventListener("click", () => {
            if (thumb.dataset.full) {
                mainImg.src = thumb.dataset.full;
                mainImg.removeAttribute("srcset");
            }
            setActive(index);
        });
    });

    setActive(0);
});

// == "50-50 variant" home showcase videos: play only once scrolled into view,
// instead of every card video racing to autoplay at page load. oi.video
// enforces a page-wide "only one video plays at a time" policy -- any play()
// (including one triggered by a video's own autoplay attribute) pauses every
// other currently-playing video. With one of these per featured home, letting
// them all autoplay immediately meant whichever finished initializing last
// silently paused the others. Triggering play() from real scroll position
// instead sequences them naturally, since only one is ever in view at a time.
const scrollVideoContainers = document.querySelectorAll(".parade-2026-scroll-video .oi-video");
if (scrollVideoContainers.length && "IntersectionObserver" in window) {
    // A jump straight to a home's anchor (sub-nav, offcanvas, swiper CTA) can land on a
    // video that hasn't finished mounting yet (network fetch + vendor API init take real
    // time), so play() may be called a moment too early. Retry once the video reports
    // itself mounted; if it was already playing by then this is a harmless no-op.
    const playWhenReady = (videoId) => {
        oi.video.play(videoId);
        window.addEventListener("oi.loaded.video." + videoId, () => oi.video.play(videoId), { once: true });
    };

    const scrollVideoObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    playWhenReady(entry.target.id.replace(/-container$/, ""));
                }
            });
        },
        { threshold: 0.5 }
    );
    scrollVideoContainers.forEach((container) => scrollVideoObserver.observe(container));
}

// == "the homes" swiper: syncs the left preview panel (background, logo,
// description, CTA link) with whichever slide was clicked or paged to.
// Guarded on every element it touches so this is a no-op on any page/state
// where the swiper or preview panel isn't rendered (e.g. no parade homes data).
const mySwiperEl = document.querySelector(".mySwiper");
const previewBg = document.getElementById("previewBg");
const previewBgImg = document.getElementById("previewBgImg");
const previewContent = document.getElementById("previewContent");
const previewLogoWrap = document.getElementById("previewLogoWrap");
const previewLogo = document.getElementById("previewLogo");
const previewDesc = document.getElementById("previewDesc");
const previewBtn = document.getElementById("previewBtn");
const swiperPrevBtn = document.getElementById("prevBtn");
const swiperNextBtn = document.getElementById("nextBtn");

if (
    mySwiperEl &&
    previewBg &&
    previewContent &&
    previewLogoWrap &&
    previewLogo &&
    previewDesc &&
    previewBtn &&
    swiperPrevBtn &&
    swiperNextBtn
) {
    // Selection is tracked with our own class rather than Swiper's built-in
    // swiper-slide-active, because that class reflects scroll position --
    // once every slide already fits on screen there's nowhere left to
    // scroll to, so it can't distinguish which slide was actually clicked.
    const setActiveSlide = (slideEl) => {
        mySwiperEl.querySelectorAll(".swiper-slide").forEach((el) => {
            el.classList.toggle("is-selected", el === slideEl);
        });
    };

    // Reads a slide's own data-* attributes directly -- no separate index-keyed
    // data store to keep in sync, so it can't drift out of step with Swiper's
    // clamped active index (which is what broke the last slide in multi-per-view).
    const updatePreview = (slideEl) => {
        if (!slideEl) {
            return;
        }
        const { desc, bg, link, logo } = slideEl.dataset;
        setActiveSlide(slideEl);
        previewContent.style.opacity = "0";
        setTimeout(() => {
            if (previewBgImg && bg) {
                previewBgImg.src = bg;
                previewBgImg.removeAttribute("srcset");
            }
            previewDesc.textContent = desc;
            previewBtn.setAttribute("href", link);
            if (logo) {
                previewLogo.src = logo;
                previewLogoWrap.style.display = "";
            } else {
                previewLogo.src = "";
                previewLogoWrap.style.display = "none";
            }
            previewContent.style.opacity = "1";
        }, 200);
    };

    new Swiper(mySwiperEl, {
        slidesPerView: 1,
        spaceBetween: 20,
        loop: true,
        // No slideToClickedSlide: we already update the preview/selection from
        // the clicked slide's own data directly, so we don't need Swiper to also
        // auto-scroll to it -- that auto-scroll was what shifted the wrapper and
        // pushed the first slide's clickable area behind the prev button.
        // Re-measures slide width/position if content shifts after init (e.g. a
        // lazy-loaded thumbnail finishing its layout) -- without this, clicks can
        // land on stale slide boundaries until something else forces a reflow.
        observer: true,
        observeParents: true,
        breakpoints: {
            576: { slidesPerView: 2, spaceBetween: 15 },
            992: { slidesPerView: 3, spaceBetween: 20 }
        },
        navigation: {
            nextEl: "#nextBtn",
            prevEl: "#prevBtn",
        },
        on: {
            // A direct slide click -- always the slide the user actually clicked.
            click: (swiperInstance) => updatePreview(swiperInstance.clickedSlide),
            // Arrow-driven paging -- reflect whichever slide is now active.
            slideChange: function () {
                updatePreview(this.slides[this.activeIndex]);
            }
        }
    });
}

// == spec detail page: mobile-only gallery swiper with prev/next arrows.
// Plain looping carousel, no preview panel to sync -- it's a standalone
// image gallery, unlike the homes swiper above.
const galleryMobileSwiperEl = document.querySelector(".galleryMobileSwiper");
const galleryMobilePrevBtn = document.getElementById("galleryMobilePrevBtn");
const galleryMobileNextBtn = document.getElementById("galleryMobileNextBtn");

if (galleryMobileSwiperEl && galleryMobilePrevBtn && galleryMobileNextBtn) {
    new Swiper(galleryMobileSwiperEl, {
        slidesPerView: 1,
        spaceBetween: 16,
        loop: true,
        observer: true,
        observeParents: true,
        navigation: {
            nextEl: "#galleryMobileNextBtn",
            prevEl: "#galleryMobilePrevBtn",
        },
    });
}

// == "the homes" mobile preview bar: simple prev/next index cycling. Mobile
// hides the thumbnail strip entirely (just a mosaic image + title/logo/desc),
// so there's no swipeable viewport to drive with Swiper -- a plain index
// over the hidden per-home data divs is simpler and avoids a second,
// mostly-invisible Swiper instance.
const mobileSlidesPanel = document.getElementById("mobileSlidesPanel");
const mobileTitle = document.getElementById("mobilePreviewTitle");
const mobileDesc = document.getElementById("mobilePreviewDesc");
const mobileBtn = document.getElementById("mobilePreviewBtn");
const mobileLogoWrap = document.getElementById("mobilePreviewLogoWrap");
const mobileLogo = document.getElementById("mobilePreviewLogo");
const mobileTitleLogoWrap = document.getElementById("mobilePreviewTitleLogoWrap");
const mobileTitleLogo = document.getElementById("mobilePreviewTitleLogo");
const mobileMosaicImg = document.getElementById("mobileSlidesMosaicImg");
const mobilePrevBtn = document.getElementById("mobilePrevBtn");
const mobileNextBtn = document.getElementById("mobileNextBtn");

if (
    mobileSlidesPanel &&
    mobileTitle &&
    mobileDesc &&
    mobileBtn &&
    mobileLogoWrap &&
    mobileLogo &&
    mobileTitleLogoWrap &&
    mobileTitleLogo &&
    mobilePrevBtn &&
    mobileNextBtn
) {
    const mobileSlidesData = Array.from(mobileSlidesPanel.querySelectorAll(".mobile-slide-data")).map(
        (el) => ({ ...el.dataset })
    );
    let mobileIndex = 0;

    const renderMobileSlide = () => {
        const data = mobileSlidesData[mobileIndex];
        if (!data) {
            return;
        }
        mobileTitle.textContent = data.title;
        mobileDesc.textContent = data.desc;
        mobileBtn.setAttribute("href", data.link);
        if (data.logo) {
            mobileLogo.src = data.logo;
            mobileLogoWrap.style.display = "";
        } else {
            mobileLogo.src = "";
            mobileLogoWrap.style.display = "none";
        }
        if (data.logoLight) {
            mobileTitleLogo.src = data.logoLight;
            mobileTitleLogoWrap.style.display = "";
        } else {
            mobileTitleLogo.src = "";
            mobileTitleLogoWrap.style.display = "none";
        }
        if (mobileMosaicImg && data.bg) {
            mobileMosaicImg.src = data.bg;
        }
    };

    mobilePrevBtn.addEventListener("click", () => {
        mobileIndex = (mobileIndex - 1 + mobileSlidesData.length) % mobileSlidesData.length;
        renderMobileSlide();
    });

    mobileNextBtn.addEventListener("click", () => {
        mobileIndex = (mobileIndex + 1) % mobileSlidesData.length;
        renderMobileSlide();
    });
}
